import React, { useRef } from 'react'
import { Button, CardRadioInput, Pill } from '@demandlocal/components'
import { ReactComponent as IconClose } from './../../images/icons/icon_close.svg'
import { ReactComponent as AudienceCustomTypeIcon } from '../../images/audience/audience_custom_type_icon.svg'
import { ReactComponent as IconPeople } from '../../images/icons/icon_people.svg'
import { ReactComponent as IconCar } from '../../images/icons/icon_car.svg'
import { ReactComponent as IconService } from '../../images/icons/icon_service.svg'
import { useGetOverallStats } from '../../api/dealership/useGetOverallStats'
import pluralize from 'pluralize'
import useCalculateContentSpace from '../../lib/hooks/useCalculateContentSpace'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { useCreateAudienceCreationSession } from '../../api/dealership/audiences/useCreateAudienceCreationSession'
import { useDealership } from '../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'
type Props = {
  onAudienceTypeSelected: (
    _id: string,
    type: 'leads' | 'sales' | 'service' | 'custom',
  ) => void
}

type FieldValues = {
  audience_type: 'leads' | 'sales' | 'service' | 'custom'
}

const AudienceSelectType = ({ onAudienceTypeSelected }: Props): JSX.Element => {
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const { data: { sources = { total: 0, sales: 0, service: 0, leads: 0 } } = {} } =
    useGetOverallStats()

  const { mutateAsync } = useCreateAudienceCreationSession()

  const formContext = useForm<FieldValues>({
    mode: 'onSubmit',
  })
  const navigate = useNavigate()

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = formContext

  const headerRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const { availableSpace } = useCalculateContentSpace({
    refs: [headerRef, footerRef],
  })

  const onSubmit = async (values: FieldValues): Promise<void> => {
    const { audience_type } = values
    const session = await mutateAsync({
      type: audience_type,
    })
    onAudienceTypeSelected(session.data._id, audience_type)
  }

  return (
    <FormProvider {...formContext}>
      <form onSubmit={handleSubmit(onSubmit)} className={'flex flex-1 flex-col'}>
        <div className={'py-10 flex justify-between items-center px-12'} ref={headerRef}>
          <h1 className={'text-3xl font-bold'}>Build an Audience</h1>
          <IconClose
            className={'cursor-pointer'}
            onClick={() => {
              navigate(t('dealership.audiences.custom', { dealership }))
            }}
          />
        </div>
        <div className={'overflow-scroll px-12 flex-1'}>
          <div className={`flex-col flex justify-start gap-6 h-full`}>
            <div className={'mt-4 font-bold text-base'}>
              Who do you want to market to?
            </div>
            <div className={'grid grid-cols-2 gap-4'}>
              <CardRadioInput
                icon={({ selected }) => (
                  <div
                    className={`w-20 h-20 rounded-full items-center flex justify-center transition ease-in-out duration-500 ${
                      selected ? 'bg-white' : 'bg-primary-persian-blue/10'
                    }`}>
                    <IconPeople />
                  </div>
                )}
                name={'audience_type'}
                value={'leads'}
                label={'Potential Customers'}
                description={
                  <>
                    <span>People who have submitted a lead.</span>
                    <div>
                      <Pill type={sources.leads ? 'success' : 'warning'}>
                        {sources.leads || 0} {pluralize('source', sources.leads)}{' '}
                        available
                      </Pill>
                    </div>
                  </>
                }
                background={'white'}
              />
              <CardRadioInput
                icon={({ selected }) => (
                  <div
                    className={`w-20 h-20 rounded-full items-center flex justify-center transition ease-in-out duration-500 ${
                      selected ? 'bg-white' : 'bg-primary-persian-blue/10'
                    }`}>
                    <IconCar />
                  </div>
                )}
                name={'audience_type'}
                value={'sales'}
                label={'Sales Customers'}
                description={
                  <>
                    <span>People who have purchased from you.</span>
                    <div>
                      <Pill type={sources.sales ? 'success' : 'warning'}>
                        {sources.sales || 0} {pluralize('source', sources.sales)}{' '}
                        available
                      </Pill>
                    </div>
                  </>
                }
                background={'white'}
              />
              <CardRadioInput
                icon={({ selected }) => (
                  <div
                    className={`w-20 h-20 rounded-full items-center flex justify-center transition ease-in-out duration-500 ${
                      selected ? 'bg-white' : 'bg-primary-persian-blue/10'
                    }`}>
                    <IconService />
                  </div>
                )}
                name={'audience_type'}
                value={'service'}
                label={'Service Customers'}
                description={
                  <>
                    <span>People who have had service at your dealership.</span>
                    <div>
                      <Pill type={sources.service ? 'success' : 'warning'}>
                        {sources.service || 0} {pluralize('source', sources.service)}{' '}
                        available
                      </Pill>
                    </div>
                  </>
                }
                background={'white'}
              />
              <CardRadioInput
                icon={({ selected }) => (
                  <div
                    className={`w-20 h-20 rounded-full items-center flex justify-center transition ease-in-out duration-500 ${
                      selected ? 'bg-white' : 'bg-primary-persian-blue/10'
                    }`}>
                    <AudienceCustomTypeIcon />
                  </div>
                )}
                name={'audience_type'}
                value={'custom'}
                label={'Custom'}
                description={
                  <>
                    <span>Create an Audience using any filters.</span>
                    <div>
                      <Pill type={'success'}>
                        {sources.total || 0} {pluralize('source', sources.total)}{' '}
                        available
                      </Pill>
                    </div>
                  </>
                }
                background={'white'}
              />
            </div>
          </div>
        </div>
        <div ref={footerRef} className={'flex justify-between py-5 border-t px-12'}>
          <Button
            disabled={isSubmitting}
            variant={'tertiary'}
            onClick={(e) => {
              e.preventDefault()
              navigate(t('dealership.audiences.custom', { dealership }))
            }}>
            Cancel
          </Button>
          <Button disabled={!isDirty || isSubmitting} variant={'primary'}>
            Continue
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default AudienceSelectType
